import { Link } from '@tanstack/react-router';
import { Button } from '@/shared/components/ui/Button';
import { RefreshCw } from 'lucide-react';

interface ErrorScreenProps {
  /** Error title to display */
  title?: string;
  /** Custom error message */
  message?: string;
  /** Error object or string */
  error: unknown;
  /** Optional retry callback - if provided, shows retry button instead of navigation link */
  onRetry?: () => void;
  /** Link destination when retry is not provided */
  linkTo?: string;
  /** Link text when retry is not provided */
  linkText?: string;
}

export default function ErrorScreen({
  error,
  title,
  message,
  onRetry,
  linkTo = '/auth',
  linkText = 'Back to Login',
}: ErrorScreenProps) {
  const errorMessage = message || (error instanceof Error ? error.message : 'An unexpected error occurred');

  return (
    <div className="min-h-screen mx-auto flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-destructive mb-4">
          {title || 'Application Error'}
        </h1>
        <p className="text-muted-foreground mb-6">
          {errorMessage}
        </p>
        
        {onRetry ? (
          <Button 
            onClick={onRetry}
            variant="default"
            className="inline-flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        ) : (
          <Button asChild variant="default">
            <Link to={linkTo} className="inline-flex items-center gap-2">
              <span aria-hidden="true">&larr;</span>
              {linkText}
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}